import { useState } from 'react';
import { useScrollReveal, useStaggerReveal } from '@/hooks/useScrollReveal';
import { generateWhatsAppLink } from './HeroSection';

const features = [
  {
    icon: 'ri-anchor-line',
    title: 'Denize 70 Metre',
    desc: 'Otelimiz denize yalnızca 70 metre mesafededir. Gün içinde denize kolayca ulaşabilir, tatilin keyfini pratik şekilde çıkarabilirsiniz.',
  },
  {
    icon: 'ri-map-pin-line',
    title: "Kız Kumu'na Yürüme Mesafesi",
    desc: "Dünyaca ünlü Kız Kumu Plajı'na yürüme mesafesindeki konumuyla Orhaniye tatilinizi daha özel hale getirir.",
  },
  {
    icon: 'ri-drop-line',
    title: 'Açık Yüzme Havuzu',
    desc: 'Misafirlerimizin keyifli vakit geçirebilmesi için açık yüzme havuzumuz ve dinlenme alanlarımız bulunmaktadır.',
  },
  {
    icon: 'ri-restaurant-2-line',
    title: 'Serpme Köy Kahvaltısı',
    desc: 'Güne yöresel ürünlerle hazırlanan zengin serpme köy kahvaltısıyla başlayabilirsiniz.',
  },
  {
    icon: 'ri-home-smile-line',
    title: 'Aile Odası Seçeneği',
    desc: '1+1 mutfaklı aile odalarımız, aileler ve uzun süreli konaklamalar için konforlu yaşam alanı sunar.',
  },
  {
    icon: 'ri-heart-3-line',
    title: 'Sıcak Aile Ortamı',
    desc: 'Samimi, huzurlu ve güven veren butik otel atmosferiyle misafirlerimizi en iyi şekilde ağırlıyoruz.',
  },
];

export default function FeaturesSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollReveal({ threshold: 0.3 });

  return (
    <section id="avantajlar" className="w-full py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className={`text-center mb-12 md:mb-16 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
            İlkim Butik Otel'de Tatili{' '}
            <span className="text-accent-500">Özel</span> Kılan Detaylar
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Şehrin kalabalığından uzak, Marmaris Orhaniye'nin yemyeşil doğasında; denize, Kız Kumu'na ve huzura yakın bir konaklama deneyimi.
          </p>
        </div>

        {/* Feature Grid */}
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={`group bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/60 hover:border-primary-200 hover:shadow-md transition-all duration-500 ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
                <i className={`${f.icon} text-xl`} />
              </div>
              <h3 className="text-base md:text-lg font-bold text-foreground-900 mb-2">
                {f.title}
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                {f.desc}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div ref={ctaRef} className={`text-center mt-10 md:mt-14 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <a
            href="#fiyat-form"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#fiyat-form')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
          >
            <i className="ri-calendar-check-line" />
            Tarih Seçerek Fiyat Teklifi Al
          </a>
        </div>
      </div>
    </section>
  );
}

export function PriceFormSection() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [checkin, setCheckin] = useState('');
  const [checkout, setCheckout] = useState('');
  const [guests, setGuests] = useState('2');
  const [room, setRoom] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [noteCount, setNoteCount] = useState(0);

  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: formRef, isVisible: formVisible } = useScrollReveal({ threshold: 0.15 });

  const today = new Date().toISOString().split('T')[0];

  const handleSubmit = () => {
    if (!checkin || !checkout || !guests || !room) {
      setError('Lütfen giriş tarihi, çıkış tarihi, kişi sayısı ve oda tercihi seçin.');
      return;
    }
    setError('');
    const link = generateWhatsAppLink(checkin, checkout, guests, room, name, phone, note);
    window.open(link, '_blank');
  };

  return (
    <section id="fiyat-form" className="w-full py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-secondary-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className={`text-center mb-10 md:mb-14 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
            Tatil Tarihinizi Seçin,{' '}
            <span className="text-primary-500">Size Uygun Fiyat</span> Teklifini WhatsApp'tan Alın
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Giriş ve çıkış tarihinizi seçin. Kişi sayısı ve oda tercihinizi ekleyin. Seçtiğiniz tarihlere göre fiyat ve müsaitlik bilgisi için WhatsApp üzerinden hızlıca destek alın.
          </p>
        </div>

        {/* Form */}
        <div ref={formRef} className={`bg-background-50 rounded-3xl p-5 md:p-8 lg:p-10 shadow-lg border border-background-200/70 transition-all duration-700 delay-200 ${formVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-[0.98]'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {/* Name */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">
                Ad Soyad
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Adınız ve soyadınız"
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              />
            </div>

            {/* Phone */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">
                Telefon
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="05XX XXX XX XX"
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              />
            </div>

            {/* Check-in */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">
                Giriş Tarihi *
              </label>
              <input
                type="date"
                value={checkin}
                min={today}
                onChange={(e) => {
                  setCheckin(e.target.value);
                  if (checkout && e.target.value >= checkout) {
                    setCheckout('');
                  }
                  setError('');
                }}
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              />
            </div>

            {/* Check-out */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">
                Çıkış Tarihi *
              </label>
              <input
                type="date"
                value={checkout}
                min={checkin || today}
                onChange={(e) => { setCheckout(e.target.value); setError(''); }}
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
              />
            </div>

            {/* Guests */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">
                Kişi Sayısı *
              </label>
              <select
                value={guests}
                onChange={(e) => { setGuests(e.target.value); setError(''); }}
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all appearance-none cursor-pointer"
                style={{ backgroundImage: 'none' }}
              >
                {[1,2,3,4,5,6,7,8].map((n) => (
                  <option key={n} value={n}>{n} Kişi</option>
                ))}
                <option value="9+">9+ Kişi</option>
              </select>
            </div>

            {/* Room Type */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">
                Oda Tercihi *
              </label>
              <select
                value={room}
                onChange={(e) => { setRoom(e.target.value); setError(''); }}
                className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all appearance-none cursor-pointer"
                style={{ backgroundImage: 'none' }}
              >
                <option value="">Oda seçin</option>
                <option value="1+1 Mutfaklı Aile Odası">1+1 Mutfaklı Aile Odası</option>
                <option value="Standart Oda">Standart Oda</option>
                <option value="Emin Değilim, Bilgi Almak İstiyorum">Emin Değilim, Bilgi Almak İstiyorum</option>
              </select>
            </div>
          </div>

          {/* Note */}
          <div className="flex flex-col gap-1.5 mt-4 md:mt-5">
            <label className="text-xs font-semibold text-foreground-600 uppercase tracking-wider">
              Ek Not
            </label>
            <textarea
              value={note}
              onChange={(e) => {
                const val = e.target.value.slice(0, 500);
                setNote(val);
                setNoteCount(val.length);
              }}
              placeholder="Çocuk sayısı, özel talep veya merak ettiğiniz konuları yazabilirsiniz."
              rows={3}
              className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
            />
            <p className="text-xs text-foreground-400 text-right">{noteCount}/500</p>
          </div>

          {/* Honeypot */}
          <div className="company-alt">
            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              onChange={() => {}}
            />
          </div>

          {/* Error */}
          {error && (
            <p className="mt-3 text-xs text-red-500 font-medium">{error}</p>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            className="w-full mt-5 px-6 py-4 bg-primary-500 text-background-50 rounded-2xl font-semibold text-sm md:text-base hover:bg-primary-600 transition-all flex items-center justify-center gap-2 cursor-pointer hover:scale-[1.01] active:scale-[0.99]"
          >
            <i className="ri-whatsapp-line text-lg" />
            WhatsApp'tan Fiyat Teklifi Al
          </button>
        </div>
      </div>
    </section>
  );
}